import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { fetchMockTests } from "./nativeLearning";
import MockTestSection from "./MockTestSection";

// The stored start is an IST wall-clock — format its components directly so all
// viewers see the same IST time the creator set (not their browser timezone).
function fmtDateTime(s) {
  if (!s) return "";
  const m = String(s).replace("T", " ").match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
  if (!m) return String(s);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  let hr = parseInt(m[4], 10);
  const ampm = hr >= 12 ? "PM" : "AM";
  hr = hr % 12 || 12;
  return `${months[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}, ${hr}:${m[5]} ${ampm} IST`;
}

// Darken a hex colour so the badge/button can use a rich gradient built from
// the single brand colour.
function shade(hex, pct) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  const f = 1 + pct;
  const c = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
  return `#${((1 << 24) + (c((n >> 16) & 255) << 16) + (c((n >> 8) & 255) << 8) + c(n & 255)).toString(16).slice(1)}`;
}
const grad = (p) => `linear-gradient(150deg, ${shade(p, 0.18)} 0%, ${p} 44%, ${shade(p, -0.26)} 100%)`;
const btnBg = (p) => `linear-gradient(180deg, rgba(255,255,255,.22) 0%, rgba(255,255,255,0) 45%), ${grad(p)}`;

const ClockGlyph = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
  </svg>
);

/**
 * Page-aware entry point for Live/Mock Tests, placed at the top of each
 * member-site page (courses | community | members | map | leaderboard | rewards).
 * Shows only when a PUBLISHED test targets THIS page via the admin's "Show on"
 * selector (a test with no pages selected falls back to Courses).
 *
 * Two surfaces, both from the same test list:
 *   - Upcoming (scheduled) tests -> a "notice" banner announcing when each opens,
 *     the same idea as the Upcoming Events banner.
 *   - Available (open) tests     -> a launcher card that opens the timed-test
 *     experience (MockTestSection) in a full-screen overlay.
 */
export default function MockTestLauncher({ funnelId, memberId, jwt, primary = "#0274F7", page = "courses", courseId = null, groupToken = null }) {
  const [tests, setTests] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!funnelId || !jwt) return;
    let alive = true;
    fetchMockTests({ funnelId, memberId, jwt })
      .then((d) => { if (alive) setTests(Array.isArray(d.mock_tests) ? d.mock_tests : []); })
      .catch(() => { if (alive) setTests([]); });
    return () => { alive = false; };
  }, [funnelId, memberId, jwt]);

  // Popup UX: Esc closes, and lock background scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const relevant = tests.filter((t) => {
    if (t.is_closed) return false; // window has ended — hide it everywhere
    // Empty "Show on" falls back to the Courses page.
    const pages = Array.isArray(t.display_pages) && t.display_pages.length ? t.display_pages : ["courses"];
    if (!pages.includes(page)) return false;
    // Courses/Community can be scoped to specific courses/groups: with none
    // chosen the banner shows on the listing page; with some chosen it shows
    // inside those specific course/group pages.
    if (page === "courses") {
      const cids = Array.isArray(t.course_ids) ? t.course_ids : [];
      return courseId ? cids.includes(courseId) : cids.length === 0;
    }
    if (page === "community") {
      const gtokens = Array.isArray(t.group_tokens) ? t.group_tokens : [];
      // No specific groups chosen -> show across the community (every group);
      // specific groups -> only inside those groups' feeds.
      if (gtokens.length === 0) return true;
      return groupToken != null && gtokens.includes(groupToken);
    }
    return true;
  });
  const upcoming = relevant.filter((t) => t.available_now === false);
  const available = relevant.filter((t) => t.available_now !== false);

  if (relevant.length === 0) return null;

  return (
    <>
      {/* Scheduled-test notices — like the Upcoming Events banner. */}
      {upcoming.map((t) => (
        <button
          key={t.mock_test_id}
          onClick={() => setOpen(true)}
          className="group mb-4 flex w-full items-center gap-3.5 rounded-2xl border border-amber-200/80 bg-gradient-to-r from-amber-50 to-orange-50/50 p-4 dark:border-amber-500/25 dark:from-amber-500/[0.10] dark:to-orange-500/[0.04] text-left shadow-[0_1px_2px_rgba(16,24,40,.04),0_12px_24px_-18px_rgba(217,119,6,.5)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_30px_-18px_rgba(217,119,6,.55)]"
        >
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md">
            <ClockGlyph className="w-5 h-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="rounded-full bg-amber-200/80 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-1 dark:ring-amber-500/30">Upcoming test</span>
            </span>
            <span className="mt-1 block truncate text-[14.5px] font-bold text-amber-950 dark:text-amber-50">{t.name}</span>
            <span className="block text-[12.5px] text-amber-700 dark:text-amber-200/70">Opens {fmtDateTime(t.available_from)} · {t.total_questions} questions · {t.duration_minutes} min</span>
          </span>
          <svg className="hidden w-5 h-5 shrink-0 text-amber-400 transition group-hover:translate-x-0.5 sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </button>
      ))}

      {/* Open tests — the launcher card. */}
      {available.length > 0 ? (
        <button
          onClick={() => setOpen(true)}
          className="group relative mb-6 flex w-full items-center gap-4 overflow-hidden rounded-2xl border p-4 text-left transition-transform duration-200 hover:-translate-y-0.5"
          style={{
            borderColor: `${primary}2E`,
            background: `linear-gradient(120deg, ${primary}12, ${primary}05)`,
            boxShadow: `0 1px 2px rgba(16,24,40,.04), 0 18px 34px -22px ${primary}`,
          }}
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-white ring-1 ring-white/25" style={{ background: grad(primary), boxShadow: `0 12px 24px -8px ${primary}, inset 0 1px 0 rgba(255,255,255,.28)` }}>
            <ClockGlyph />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="text-[15px] font-extrabold tracking-tight text-gray-900">Live Mock Tests</span>
              <span className="inline-flex h-5 items-center rounded-full px-2 text-[10px] font-bold text-white" style={{ background: grad(primary) }}>{available.length}</span>
            </span>
            <span className="mt-0.5 block text-[13px] text-gray-500">
              {available.length} timed test{available.length === 1 ? "" : "s"} available — sit a full exam under time.
            </span>
          </span>
          <span className="hidden shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold text-white ring-1 ring-white/20 transition group-hover:gap-2.5 sm:inline-flex" style={{ background: btnBg(primary), boxShadow: `0 8px 18px -8px ${primary}` }}>
            Open
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          </span>
        </button>
      ) : null}

      {open
        ? createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-slate/60 p-3 backdrop-blur-md sm:p-4">
              <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-surface shadow-2xl ring-1 ring-black/5">
                <header className="flex items-center justify-between px-5 py-4" style={{ background: `linear-gradient(120deg, ${primary}14, transparent)` }}>
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-2xl text-white ring-1 ring-white/25" style={{ background: grad(primary), boxShadow: `0 10px 20px -8px ${primary}, inset 0 1px 0 rgba(255,255,255,.28)` }}>
                      <ClockGlyph className="w-5 h-5" />
                    </span>
                    <div>
                      <h2 className="text-[15px] font-extrabold tracking-tight text-gray-900">Live Mock Tests</h2>
                      <p className="text-[11.5px] text-gray-400">Timed exams — good luck!</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="rounded-full p-2 text-gray-400 transition hover:bg-surface hover:text-gray-700 hover:shadow-sm"
                    aria-label="Close mock tests"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </header>
                <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50 to-gray-100/40 px-4 py-6 sm:px-6">
                  <MockTestSection funnelId={funnelId} memberId={memberId} jwt={jwt} primary={primary} page={page} courseId={courseId} groupToken={groupToken} />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
